export const STALE_VOB_DEFAULT_DAYS = 30;

export interface VobListCohort {
  isMostRecentPerPolicy: boolean;
  isTreatable: boolean;
  isActiveMember: boolean;
  isActivePolicy: boolean;
  includeDeletedPolicies: boolean;
  includeExcludedFromAnalyticsMembers: boolean;
  staleDays?: number;
}

export const EMPTY_COHORT: VobListCohort = {
  isMostRecentPerPolicy: false,
  isTreatable: false,
  isActiveMember: false,
  isActivePolicy: false,
  includeDeletedPolicies: false,
  includeExcludedFromAnalyticsMembers: false
};

export const DEFAULT_COHORT: VobListCohort = {
  ...EMPTY_COHORT,
  isMostRecentPerPolicy: true,
  isActiveMember: true,
  isActivePolicy: true
};

export const STALE_WORKLIST_COHORT: VobListCohort = {
  isMostRecentPerPolicy: true,
  isTreatable: true,
  isActiveMember: true,
  isActivePolicy: true,
  includeDeletedPolicies: false,
  includeExcludedFromAnalyticsMembers: false,
  staleDays: STALE_VOB_DEFAULT_DAYS
};

export interface VobListToggleCopy {
  label: string;
  description: string;
}

export const COHORT_TOGGLE_COPY = {
  mostRecentPerPolicy: {
    label: 'Most recent VOB only',
    description:
      'Keeps only the newest verification on each policy, so a policy shows up once instead of once per VOB.'
  },
  stale: {
    label: `Stale (>${STALE_VOB_DEFAULT_DAYS}d)`,
    description: `Keeps verifications last run more than ${STALE_VOB_DEFAULT_DAYS} days ago.`
  },
  treatable: {
    label: 'Treatable only',
    description:
      "Keeps people we can currently treat: their primary policy's carrier and state match an active treatability match. Says nothing about enrollment."
  },
  activeMember: {
    label: 'Active members only',
    description: 'Keeps people enrolled right now, with an open episode of care.'
  },
  activePolicy: {
    label: 'Active policies only',
    description:
      'Keeps policies still in force: coverage started, has not ended, and the order is not Inactive. A plan with no dates on file still counts as active, since that is how we read it everywhere else.'
  },
  includeDeleted: {
    label: 'Show deleted',
    description:
      'Adds back verifications whose policy was deleted. Hidden by default; these rows carry a red Deleted pill.'
  },
  includeExcludedFromAnalyticsMembers: {
    label: 'Show analytics-excluded',
    description:
      'Adds back people flagged exclude_from_analytics, typically test and internal records. Hidden by default.'
  }
} as const satisfies Record<string, VobListToggleCopy>;

export const isStaleWorklistCohort = (cohort: VobListCohort): boolean =>
  cohort.isMostRecentPerPolicy &&
  cohort.isTreatable &&
  cohort.isActiveMember &&
  cohort.isActivePolicy &&
  !cohort.includeDeletedPolicies &&
  !cohort.includeExcludedFromAnalyticsMembers &&
  cohort.staleDays === STALE_VOB_DEFAULT_DAYS;

export const buildCohortFilters = (cohort: VobListCohort): Record<string, string> => {
  const filters: Record<string, string> = {};

  if (cohort.isMostRecentPerPolicy) filters.is_most_recent_per_policy = 'true';
  if (cohort.isTreatable) filters.is_treatable = 'true';
  if (cohort.isActiveMember) filters.is_active_member = 'true';
  if (cohort.isActivePolicy) filters.is_active_policy = 'true';
  if (cohort.includeDeletedPolicies) filters.include_deleted_policies = 'true';
  if (cohort.includeExcludedFromAnalyticsMembers)
    filters.include_excluded_from_analytics_members = 'true';
  if (cohort.staleDays != null) filters.vob_stale_days = String(cohort.staleDays);

  return filters;
};
